import React, { useState, useEffect, useCallback } from 'react';
import { Input } from 'antd';
import type { InputProps as AntdInputProps } from 'antd';

export interface PasswordInputProps extends Omit<AntdInputProps, 'onChange' | 'value'> {
  label?: string;
  value: string;
  onChange: (value: string, isValid: boolean) => void;
  onValidationChange?: (isValid: boolean, message: string) => void;
  showValidation?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  minLength?: number;
  requireUppercase?: boolean;
  requireLowercase?: boolean;
  requireNumber?: boolean;
  requireSpecialChar?: boolean;
  showStrengthIndicator?: boolean;
  customValidator?: (password: string) => { isValid: boolean; message: string };
  fullWidth?: boolean;
}

type ValidationState = 'idle' | 'valid' | 'invalid';
type PasswordStrength = 'weak' | 'medium' | 'strong';

export const PasswordInput: React.FC<PasswordInputProps> = ({
  label,
  value,
  onChange,
  onValidationChange,
  showValidation = true,
  validateOnBlur = true,
  validateOnChange = true,
  required = false,
  error,
  helperText,
  minLength = 6,
  requireUppercase = false,
  requireLowercase = false,
  requireNumber = false,
  requireSpecialChar = false,
  showStrengthIndicator = false,
  customValidator,
  className = '',
  disabled,
  placeholder = '••••••••',
  fullWidth = false,
  size = 'large',
  id,
  ...props
}) => {
  const [validationState, setValidationState] = useState<ValidationState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [touched, setTouched] = useState(false);
  const [strength, setStrength] = useState<PasswordStrength>('weak');

  // Calculate password strength
  const calculateStrength = useCallback((password: string): PasswordStrength => {
    if (!password || password.length < 6) return 'weak';

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 2) return 'weak';
    if (score <= 4) return 'medium';
    return 'strong';
  }, []);

  // Default password validation
  const defaultValidatePassword = useCallback(
    (password: string): { isValid: boolean; message: string } => {
      if (required && !password) {
        return { isValid: false, message: 'Password is required' };
      }

      if (!password) {
        return { isValid: true, message: '' };
      }

      if (password.length < minLength) {
        return { isValid: false, message: `Password must be at least ${minLength} characters` };
      }

      if (requireUppercase && !/[A-Z]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one uppercase letter' };
      }

      if (requireLowercase && !/[a-z]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one lowercase letter' };
      }

      if (requireNumber && !/[0-9]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one number' };
      }

      if (requireSpecialChar && !/[^a-zA-Z0-9]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one special character' };
      }

      return { isValid: true, message: '' };
    },
    [required, minLength, requireUppercase, requireLowercase, requireNumber, requireSpecialChar]
  );

  const validatePassword = customValidator || defaultValidatePassword;

  // Validate password
  const performValidation = useCallback(
    (password: string, updateState: boolean = true) => {
      const validation = validatePassword(password);

      if (updateState) {
        setValidationState(validation.isValid ? 'valid' : 'invalid');
        setErrorMessage(validation.message);
      }

      if (showStrengthIndicator) {
        setStrength(calculateStrength(password));
      }

      onValidationChange?.(validation.isValid, validation.message);
      return validation;
    },
    [validatePassword, onValidationChange, showStrengthIndicator, calculateStrength]
  );

  // Handle input change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (validateOnChange && touched) {
        const validation = performValidation(newValue);
        onChange(newValue, validation.isValid);
      } else {
        if (showStrengthIndicator) {
          setStrength(calculateStrength(newValue));
        }
        onChange(newValue, true);
      }
    },
    [validateOnChange, touched, performValidation, onChange, showStrengthIndicator, calculateStrength]
  );

  // Get status icon based on validation state
  const getStatusIcon = () => {
    if (!showValidation || validationState === 'idle') return undefined;
    return validationState === 'valid' ? (
      <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : validationState === 'invalid' ? (
      <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ) : undefined;
  };

  // Handle blur
  const handleBlur = useCallback(() => {
    setTouched(true);
    if (validateOnBlur) {
      const validation = performValidation(value);
      onChange(value, validation.isValid);
    }
  }, [validateOnBlur, value, performValidation, onChange]);

  // Validate when value changes externally
  useEffect(() => {
    if (touched && validateOnChange) {
      performValidation(value);
    }
  }, [value, touched, validateOnChange, performValidation]);

  const getStrengthColor = () => {
    switch (strength) {
      case 'weak':
        return 'bg-red-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'strong':
        return 'bg-green-500';
    }
  };

  const getStrengthWidth = () => {
    switch (strength) {
      case 'weak':
        return 'w-1/3';
      case 'medium':
        return 'w-2/3';
      case 'strong':
        return 'w-full';
    }
  };

  // Prioritize external error prop over internal validation error
  const displayError = error || (showValidation && validationState === 'invalid' ? errorMessage : '');
  const hasError = !!displayError;

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label className={`block mb-1 text-sm font-medium ${hasError ? 'text-red-500' : 'text-gray-700'}`} htmlFor={id}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <Input.Password
        id={id}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        status={hasError ? 'error' : undefined}
        suffix={getStatusIcon()}
        className={className}
        size={size}
        {...props}
      />

      {showStrengthIndicator && value && (
        <div className="space-y-1 mt-2">
          <div className="h-1 w-full bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${getStrengthColor()} ${getStrengthWidth()}`}
            />
          </div>
          <p className="text-xs text-slate-600">
            Password strength: <span className="font-semibold capitalize">{strength}</span>
          </p>
        </div>
      )}

      {displayError && (
        <div className="mt-1 text-xs text-red-500">{displayError}</div>
      )}

      {helperText && !displayError && (
        <div className="mt-1 text-xs text-gray-500">{helperText}</div>
      )}
    </div>
  );
};

export default PasswordInput;
